import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import { useTenant } from '../hooks/useTenant';

interface Program {
  id: string;
  name: string;
  status: string;
}

interface ProgramContextType {
  programs: Program[];
  selectedProgramId: string | null;
  selectedProgram: Program | null;
  selectProgram: (id: string | null) => void;
  loading: boolean;
  error: string | null;
  refreshPrograms: () => Promise<void>;
}

const ProgramContext = createContext<ProgramContextType | null>(null);

const STORAGE_KEY = 'impact-dashboard-selected-program';

export function ProgramProvider({ children }: { children: ReactNode }) {
  const { organization } = useTenant();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [selectedProgramId, setSelectedProgramId] = useState<string | null>(() => {
    // Restore from localStorage
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPrograms = async () => {
    if (!organization) {
      setPrograms([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const { data, error: fetchError } = await supabase
        .from('programs')
        .select('id, name, status')
        .neq('status', 'archived') // Don't show archived in selector
        .order('name');

      if (fetchError) throw fetchError;
      setPrograms(data || []);
      
      // If selected program no longer exists or is archived, clear selection
      if (selectedProgramId && !data?.find(p => p.id === selectedProgramId)) {
        setSelectedProgramId(null);
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load programs');
      setPrograms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, [organization]);

  const selectProgram = (id: string | null) => {
    setSelectedProgramId(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, id);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const selectedProgram = programs.find(p => p.id === selectedProgramId) || null;

  return (
    <ProgramContext.Provider
      value={{
        programs,
        selectedProgramId,
        selectedProgram,
        selectProgram,
        loading,
        error,
        refreshPrograms: fetchPrograms,
      }}
    >
      {children}
    </ProgramContext.Provider>
  );
}

export function useProgram() {
  const context = useContext(ProgramContext);
  if (!context) {
    throw new Error('useProgram must be used within a ProgramProvider');
  }
  return context;
}
